import {
  DriverOrderSettings,
  DriverOrderSettingsHandle,
} from "../../../components/DriverOrders/DriverOrderSettings";
import { FC, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useWorkspace } from "../../../context/WorkspaceContext";
import { useCache } from "../../../context/CacheContext";
import { useWorkspaceData } from "@data";
import { Button, ToggleSwitch } from "@components";
import SuccessToast from "../../../components/SuccessToast/SuccessToast";
import ValidationToast from "../../../components/ValidationToast/ValidationToast";
import WorkspaceSettings from "../../../models/WorkspaceSettings";
import { SettingsRowProps } from "../components/SettingsRow";

interface WorkspaceSettingsContentProps {
  SettingsRow: React.ComponentType<SettingsRowProps>;
}

const WorkspaceSettingsContent: FC<WorkspaceSettingsContentProps> = ({
  SettingsRow,
}) => {
  const driverOrderSettings = useRef<DriverOrderSettingsHandle>(null);
  const { t } = useTranslation(["common", "workspace"]);
  const { activeWorkspace } = useWorkspace();
  const { updateCacheKey } = useCache();
  const { locations } = useWorkspaceData();

  const [existingSettings, setExistingSettings] =
    useState<WorkspaceSettings | null>(null);
  const [defaultPickupLocationId, setDefaultPickupLocationId] = useState<
    string | undefined
  >(undefined);
  const [hidePickupStops, setHidePickupStops] = useState(false);
  const [hideDefaultPickupInOrderForm, setHideDefaultPickupInOrderForm] =
    useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      if (!activeWorkspace?.workspaceId) return;

      try {
        const loaded = await WorkspaceSettings.getByWorkspaceId(
          activeWorkspace.workspaceId,
        );
        if (loaded) {
          setExistingSettings(loaded);
          setDefaultPickupLocationId(loaded.defaultPickupLocationId);
          setHidePickupStops(loaded.hidePickupStops ?? false);
          setHideDefaultPickupInOrderForm(
            loaded.hideDefaultPickupInOrderForm ?? false,
          );
        }
      } catch (error) {
        console.error("Failed to load workspace settings:", error);
      }
    };

    loadSettings();
  }, [activeWorkspace?.workspaceId]);

  const handlePickupLocationChange = (selectedId: string) => {
    setDefaultPickupLocationId(selectedId || undefined);
  };

  const handleSave = async () => {
    if (!activeWorkspace?.workspaceId) return;

    setIsSaving(true);
    setShowSuccessToast(false);
    setShowErrorToast(false);
    try {
      await driverOrderSettings.current?.save();
      const saved = await WorkspaceSettings.save(
        activeWorkspace.workspaceId,
        {
          defaultPickupLocationId,
          hidePickupStops,
          hideDefaultPickupInOrderForm,
        },
        existingSettings,
      );
      setExistingSettings(saved);

      updateCacheKey();
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);
    } catch (error) {
      console.error("Failed to save workspace settings:", error);
      setShowErrorToast(true);
      setTimeout(() => setShowErrorToast(false), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div className="flex-1 sm:max-w-2xl">
          <h2 className="text-xl font-semibold text-text sm:text-2xl mb-2">
            {t("workspace:settings.workspace_settings.title")}
          </h2>
          <p className="text-secondary text-base dark:text-secondary">
            {t("workspace:settings.workspace_settings.description")}
          </p>
        </div>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isSaving}
          size="sm"
          className="mt-2 sm:mt-0 flex-shrink-0 whitespace-nowrap"
        >
          {isSaving
            ? t("workspace:settings.workspace_settings.saving")
            : t("workspace:settings.workspace_settings.save_button")}
        </Button>
      </div>

      <SuccessToast
        show={showSuccessToast}
        title={t("common:success", "Success")}
        message={t("workspace:settings.workspace_settings.saved_message")}
        onClose={() => setShowSuccessToast(false)}
      />

      <ValidationToast
        show={showErrorToast}
        title={t("common:error", "Error")}
        message={t("workspace:settings.workspace_settings.save_failed")}
        onClose={() => setShowErrorToast(false)}
      />

      <DriverOrderSettings
        key={activeWorkspace?.workspaceId}
        ref={driverOrderSettings}
        embedded
        disabled={isSaving}
      />

      <div className="divide-y divide-border">
        {locations.length > 0 && (
          <SettingsRow
            title={t(
              "workspace:settings.workspace_settings.default_pickup_location.title",
            )}
            description={t(
              "workspace:settings.workspace_settings.default_pickup_location.description",
            )}
          >
            <select
              value={defaultPickupLocationId ?? ""}
              onChange={(e) => handlePickupLocationChange(e.target.value)}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-500 dark:focus:ring-blue-500 min-w-48"
            >
              <option value="">
                {t(
                  "workspace:settings.workspace_settings.default_pickup_location.none",
                )}
              </option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.displayName ||
                    loc.addressLine ||
                    `${loc.postCode} ${loc.city}`}
                </option>
              ))}
            </select>
          </SettingsRow>
        )}

        <SettingsRow
          title={t(
            "workspace:settings.workspace_settings.hide_pickup_stops.title",
          )}
          description={t(
            "workspace:settings.workspace_settings.hide_pickup_stops.description",
          )}
        >
          <ToggleSwitch
            checked={hidePickupStops}
            onChange={() => setHidePickupStops((prev) => !prev)}
          />
        </SettingsRow>

        <SettingsRow
          title={t(
            "workspace:settings.workspace_settings.hide_default_pickup_in_order_form.title",
          )}
          description={t(
            "workspace:settings.workspace_settings.hide_default_pickup_in_order_form.description",
          )}
          separator={false}
        >
          <ToggleSwitch
            checked={hideDefaultPickupInOrderForm}
            onChange={() => setHideDefaultPickupInOrderForm((prev) => !prev)}
          />
        </SettingsRow>
      </div>
    </div>
  );
};

export default WorkspaceSettingsContent;
